import { Button, Card, Dropdown, MenuProps, Modal, Typography } from 'antd';
import {
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { CustomBreadcrumb } from 'components/CustomBreadcrumb';
import { EditTopicTiteModal } from 'components/modal/professor_area/forums/EditTopicTiteModal';
import { IProfessorAreaPosts } from 'interfaces/professor_area/forums';
import { MenuInfo } from 'rc-menu/lib/interface';
import { PageHeader } from 'components/professor_area/PageHeader';
import { PostItem } from './PostItem';
import { ShowLayout } from 'components/layout/layout/ShowLayout';
import { Stack } from '@qcx/ui';
import { TagField, useTable } from '@refinedev/antd';
import { TopicList } from './TopicList';
import { getForumTagData } from 'services/professor_area/forum';
import { useCallback } from 'react';
import { useCanMultiple } from 'components/layout/sider';
import { useDelete, useList, useModal } from '@refinedev/core';
import { useParams } from 'react-router-dom';

const { confirm } = Modal;

const items: MenuProps['items'] = [
  {
    key: 'edit',
    label: 'Editar título do tópico',
    icon: <EditOutlined />
  },
  {
    key: 'close',
    label: 'Fechar tópico',
    icon: <DeleteOutlined />
  }
];

export const TopicShow = () => {
  const { id, classroomId } = useParams();
  const editModalProps = useModal();
  const { mutateAsync } = useDelete();
  const [showPermission, createPermission, editPermission, deletePermission] =
    useCanMultiple([
      { resource: 'professor_area/forums', action: 'show' },
      { resource: 'professor_area/forums', action: 'create' },
      { resource: 'professor_area/forums', action: 'edit' },
      { resource: 'professor_area/forums', action: 'delete' }
    ]);
  const canShowForum = showPermission.data?.can ?? false;
  const canCheckShowForum = showPermission.data !== undefined;
  const canCreateForum = createPermission.data?.can ?? false;
  const canEditForum = editPermission.data?.can ?? false;
  const canDeleteForum = deletePermission.data?.can ?? false;
  const {
    tableQueryResult: { data, isLoading, refetch },
    current,
    setCurrent,
    pageSize
  } = useTable({
    resource: `professor_area/forums/${id}/posts`,
    initialPageSize: 5,
    queryOptions: {
      enabled: canShowForum
    }
  });
  const { data: queryPostData, refetch: refetchFirstPost } =
    useList<IProfessorAreaPosts>({
      resource: `professor_area/forums/${id}/posts`,
      pagination: {
        pageSize: 1,
        mode: 'server'
      },
      queryOptions: {
        enabled: canShowForum
      }
    });

  const firstPostData = queryPostData?.data;
  const posts: IProfessorAreaPosts[] = (
    current === 1 ? data?.data?.slice(1) : data?.data
  ) as IProfessorAreaPosts[];

  const tagData = getForumTagData(firstPostData?.[0]?.forum);

  const handleCloseForum = useCallback(() => {
    if (firstPostData) {
      confirm({
        title: 'Deseja fechar esse tópico?',
        content: 'Essa ação não pode ser revertida',
        icon: <ExclamationCircleOutlined style={{ color: 'red' }} />,
        okButtonProps: { type: 'primary', danger: true },
        okText: 'Fechar tópico',
        cancelText: 'Voltar',
        onOk: () => {
          mutateAsync({
            resource: 'professor_area/forums',
            id: firstPostData?.[0].forum.id,
            successNotification: () => ({
              type: 'success',
              description: 'Sucesso!',
              message: 'Forum fechado com sucesso'
            }),
            errorNotification: () => ({
              type: 'error',
              description: 'Ops!',
              message: 'Não foi possível fechar este forum. Tente novamente.'
            })
          })
            .then(() => {
              refetch();
            })
            .catch();
        }
      });
    }
  }, [firstPostData, mutateAsync, refetch]);

  const handleDropdown = useCallback(
    (event: MenuInfo) => {
      if (event.key === 'edit') {
        editModalProps.show();
      } else {
        handleCloseForum();
      }
    },
    [editModalProps, handleCloseForum]
  );
  const dropdownItems = [
    ...(canEditForum ? [items[0]] : []),
    ...(canDeleteForum ? [items[1]] : [])
  ];

  if (!canCheckShowForum || !canShowForum || !firstPostData) {
    return null;
  }

  return (
    <ShowLayout
      isLoading={isLoading}
      breadcrumb={
        <CustomBreadcrumb
          label="Área do professor"
          backButton
          items={[
            {
              label: 'Turmas',
              path: '/professor_area/classrooms'
            },
            {
              label: 'Visualizar turma',
              path: `/professor_area/classrooms/show/${classroomId}?tab=forums`
            },
            {
              label: 'Visualizar'
            },
            {
              label: 'Fórum'
            }
          ]}
        />
      }
    >
      <PageHeader
        title={firstPostData?.[0]?.forum?.title}
        icon={CommentOutlined}
        meta={
          <Stack flow="column">
            <Stack flow="row" className="!gap-1">
              <Typography.Text type="secondary">
                Tópico da turma
              </Typography.Text>
              <TagField
                color="default"
                value={firstPostData?.[0]?.forum?.grandparent.code}
              />
              <TagField
                color="default"
                value={`${firstPostData?.[0]?.forum?.grandparent?.discipline?.name}`}
              />
              <TagField color={tagData.color} value={tagData.label} />
            </Stack>
          </Stack>
        }
        actions={
          dropdownItems.length > 0 && (
            <Dropdown
              menu={
                { items: dropdownItems, onClick: handleDropdown } as MenuProps
              }
              placement="bottomLeft"
            >
              <Button icon={<EllipsisOutlined />} />
            </Dropdown>
          )
        }
      />
      <Card>
        {firstPostData && (
          <>
            <Typography.Title level={4}>Mensagem inicial</Typography.Title>
            <PostItem
              postData={firstPostData?.[0]}
              refetch={refetch}
              canCreate={canCreateForum}
              canEdit={canEditForum}
              canDelete={canDeleteForum}
            />
            <Stack className="mt-6">
              {posts && (
                <TopicList
                  data={posts}
                  total={data!.total}
                  pageSize={pageSize}
                  currentPage={current}
                  onPageChange={setCurrent}
                  refetch={refetch}
                  canCreate={canCreateForum}
                  canEdit={canEditForum}
                  canDelete={canDeleteForum}
                />
              )}
            </Stack>
            {canCreateForum && (
              <PostItem
                postData={firstPostData?.[0]}
                refetch={refetch}
                inputMode
                canCreate={canCreateForum}
                canEdit={canEditForum}
                canDelete={canDeleteForum}
              />
            )}
          </>
        )}
      </Card>

      {canEditForum && (
        <EditTopicTiteModal
          modalProps={editModalProps}
          forum={firstPostData?.[0]?.forum}
          refetch={refetchFirstPost}
        />
      )}
    </ShowLayout>
  );
};
